import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Trash2, Eye, EyeOff, Box, Group, Ungroup, GripVertical, Lock, Unlock } from 'lucide-react';
import { useDraggable } from '../hooks/useDraggable';
import './SceneObjectsList.css';

export interface SceneObject {
  id: string;
  name: string;
  type: 'environment' | 'avatar' | 'object';
  visible: boolean;
  selected: boolean;
  multiSelected?: boolean;
  groupId?: string;
  /** undefined = this kind of object can't be locked */
  locked?: boolean;
}

interface SceneObjectsListProps {
  objects: SceneObject[];
  onSelectObject: (id: string) => void;
  onMultiSelect: (id: string) => void;
  onDeleteObject: (id: string) => void;
  onToggleVisibility: (id: string) => void;
  onGroup: () => void;
  onUngroup: () => void;
  onReorder?: (orderedIds: string[]) => void;
  onToggleLock?: (id: string) => void;
  /** A locked object someone just clicked in the scene: highlight its row. */
  flashId?: string | null;
  flashAt?: number;
  multiSelectedCount: number;
}

const SceneObjectsList: React.FC<SceneObjectsListProps> = ({
  objects,
  onSelectObject,
  onMultiSelect,
  onDeleteObject,
  onToggleVisibility,
  onGroup,
  onUngroup,
  onReorder,
  onToggleLock,
  flashId,
  flashAt,
  multiSelectedCount,
}) => {
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);
  const dragItemRef = useRef<number | null>(null);
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [flashing, setFlashing] = useState<string | null>(null);
  useEffect(() => {
    if (!flashId) return;
    setFlashing(flashId);
    rowRefs.current[flashId]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    const t = setTimeout(() => setFlashing(null), 1600);
    return () => clearTimeout(t);
  }, [flashId, flashAt]);

  const getIcon = (type: string) => {
    switch (type) {
      case 'environment': return '🌍';
      case 'avatar': return '👤';
      default: return '📦';
    }
  };

  const handleDragStart = useCallback((e: React.DragEvent, idx: number) => {
    dragItemRef.current = idx;
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(idx));
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, idx: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setOverIdx(idx);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, dropIdx: number) => {
    e.preventDefault();
    const fromIdx = dragItemRef.current;
    if (fromIdx === null || fromIdx === dropIdx) {
      setDragIdx(null);
      setOverIdx(null);
      return;
    }
    const reordered = [...objects];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(dropIdx, 0, moved);
    onReorder?.(reordered.map(o => o.id));
    setDragIdx(null);
    setOverIdx(null);
  }, [objects, onReorder]);

  const handleDragEnd = useCallback(() => {
    setDragIdx(null);
    setOverIdx(null);
  }, []);

  const { dragStyle, onPointerDown: onDragPointerDown } = useDraggable();
  return (
    <div className="scene-objects-list" style={dragStyle} onPointerDown={(e) => { e.stopPropagation(); onDragPointerDown(e); }} onMouseDown={(e) => e.stopPropagation()}>
      <div className="list-header" data-drag-handle>
        <h3>Scene Objects</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {multiSelectedCount >= 2 && (
            <button className="action-btn" onClick={(e) => { e.stopPropagation(); onGroup(); }} title="Group selected">
              <Group size={14} />
            </button>
          )}
          <span className="object-count">{objects.length}</span>
        </div>
      </div>

      <div className="objects-container">
        {objects.length === 0 ? (
          <div className="empty-state">
            <Box size={24} />
            <p>No objects in scene</p>
          </div>
        ) : (
          objects.map((obj, idx) => (
            <div
              key={obj.id}
              ref={(el) => { rowRefs.current[obj.id] = el; }}
              className={`object-item ${obj.locked ? 'locked' : ''} ${flashing === obj.id ? 'lock-flash' : ''} ${obj.selected ? 'selected' : ''} ${obj.multiSelected ? 'multi-selected' : ''} ${dragIdx === idx ? 'dragging' : ''} ${overIdx === idx && dragIdx !== idx ? 'drop-target' : ''}`}
              draggable
              onDragStart={(e) => handleDragStart(e, idx)}
              onDragOver={(e) => handleDragOver(e, idx)}
              onDrop={(e) => handleDrop(e, idx)}
              onDragEnd={handleDragEnd}
              onClick={(e) => {
                if (e.ctrlKey || e.metaKey) {
                  onMultiSelect(obj.id);
                } else {
                  onSelectObject(obj.id);
                }
              }}
            >
              <div className="drag-handle">
                <GripVertical size={12} />
              </div>
              <div className="object-info">
                <span className="object-icon">{getIcon(obj.type)}</span>
                <span className="object-name">
                  {obj.groupId && <span style={{ color: '#00C2B3', fontSize: 10, marginRight: 4 }}>⛓</span>}
                  {obj.name}
                </span>
                <span className="object-type">{obj.type}</span>
              </div>

              <div className="object-actions">
                {obj.groupId && obj.selected && (
                  <button
                    className="action-btn"
                    onClick={(e) => { e.stopPropagation(); onUngroup(); }}
                    title="Ungroup"
                  >
                    <Ungroup size={14} />
                  </button>
                )}
                {obj.locked !== undefined && onToggleLock && (
                  <button
                    className={`action-btn lock ${obj.locked ? 'on' : ''}`}
                    onClick={(e) => { e.stopPropagation(); onToggleLock(obj.id); }}
                    title={obj.locked ? 'Unlock (lets you click and move it)' : 'Lock (can’t be clicked or moved)'}
                    aria-pressed={obj.locked}
                  >
                    {obj.locked ? <Lock size={14} /> : <Unlock size={14} />}
                  </button>
                )}
                <button
                  className="action-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleVisibility(obj.id);
                  }}
                  title={obj.visible ? 'Hide' : 'Show'}
                >
                  {obj.visible ? <Eye size={14} /> : <EyeOff size={14} />}
                </button>
                <button
                  className="action-btn delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteObject(obj.id);
                  }}
                  title="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
      {multiSelectedCount > 0 && (
        <div style={{ padding: '6px 12px', fontSize: 11, color: 'rgba(255,255,255,0.5)', borderTop: '1px solid var(--gx-border, rgba(255,255,255,0.1))' }}>
          {multiSelectedCount} selected — Ctrl+click to multi-select, then group
        </div>
      )}
    </div>
  );
};

export default SceneObjectsList;
